/**
 *  RequestChat.jsx
 *  @description: Chat de un reporte entre el propietario (app) y Customer
 *  Service. Al abrirlo, el servidor marca como leídos los mensajes del
 *  propietario; la respuesta le llega al propietario como push.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import cogoToast from 'cogo-toast';

import ModalContainer from '../../controlCostos/ModalContainer';
import { messages, sendMessage, NOTIFICATION_EVENT } from '../../../core/webNotifications';

const MAX_TEXT = 2000;

const RequestChat = ({ requestId, folio, onClose }) => {
   const [list, setList] = useState([]);
   const [loading, setLoading] = useState(true);
   const [text, setText] = useState('');
   const [image, setImage] = useState(null);
   const [sending, setSending] = useState(false);
   const [preview, setPreview] = useState(''); // URL local de la foto elegida
   const [zoom, setZoom] = useState(''); // foto abierta en grande
   const bottom = useRef(null);
   const fileInput = useRef(null);

   const load = async () => {
      const res = await messages(requestId);
      setLoading(false);
      if (res.ok) setList(res.data || []);
      else cogoToast.error(res.message || 'No se pudieron cargar los mensajes.', { position: 'top-right' });
   };

   useEffect(() => {
      load();
      // Si el propietario escribe mientras está abierto, se recarga.
      const onNotice = (e) => {
         const n = e.detail || {};
         if (n.type === 'message' && n.requestId === requestId) load();
      };
      window.addEventListener(NOTIFICATION_EVENT, onNotice);
      return () => window.removeEventListener(NOTIFICATION_EVENT, onNotice);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [requestId]);

   useEffect(() => {
      if (bottom.current) bottom.current.scrollIntoView({ block: 'end' });
   }, [list]);

   // Vista previa de la foto antes de enviarla.
   useEffect(() => {
      if (!image) {
         setPreview('');
         return undefined;
      }
      const url = URL.createObjectURL(image);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
   }, [image]);

   // Esc cierra el zoom sin cerrar el chat.
   useEffect(() => {
      if (!zoom) return undefined;
      const onKey = (e) => e.key === 'Escape' && setZoom('');
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
   }, [zoom]);

   const removeImage = () => {
      setImage(null);
      if (fileInput.current) fileInput.current.value = '';
   };

   const submit = async (e) => {
      e.preventDefault();
      if (!text.trim() && !image) {
         cogoToast.warn('Escribe un mensaje o adjunta una foto.', { position: 'top-right' });
         return;
      }
      setSending(true);
      const res = await sendMessage(requestId, text.trim(), image);
      setSending(false);
      if (!res.ok) {
         cogoToast.error(res.message || 'No se pudo enviar el mensaje.', { position: 'top-right' });
         return;
      }
      setText('');
      removeImage();
      setList((prev) => [...prev, res.data]);
   };

   const pickImage = (e) => {
      const f = e.target.files && e.target.files[0];
      if (f && !/^image\/(jpeg|png)$/.test(f.type)) {
         cogoToast.warn('Solo fotos JPG o PNG.', { position: 'top-right' });
         e.target.value = '';
         return;
      }
      setImage(f || null);
   };

   return (
      <ModalContainer
         size="normal"
         title={`Mensajes del reporte ${folio || requestId}`}
         btnSuccessText="Enviar"
         isLoading={sending}
         onCancel={onClose}
         onClose={onClose}
         onSuccess={submit}>
         <div className="request-chat">
            <div className="chat-list">
               {loading ? (
                  <div className="chat-empty">
                     <i className="fas fa-spinner fa-spin"></i>
                  </div>
               ) : list.length === 0 ? (
                  <div className="chat-empty">Sin mensajes. El propietario los verá en la app.</div>
               ) : (
                  list.map((m) => (
                     <div key={m.id} className={`chat-bubble ${m.author}${m.deleted ? ' deleted' : ''}`}>
                        {m.deleted ? <em>Mensaje eliminado por quien lo escribió</em> : m.text}
                        {!m.deleted && m.imageUrl && (
                           <img src={m.imageUrl} alt="Foto del mensaje" onClick={() => setZoom(m.imageUrl)} />
                        )}
                        <span className="chat-meta">
                           {m.author === 'team' ? m.authorName || 'Customer Service' : m.authorName || 'Propietario'}
                           {' · '}
                           {moment(m.sentAt).format('DD/MM/YYYY HH:mm')}
                           {m.author === 'team' && m.read && ' · Leído'}
                        </span>
                     </div>
                  ))
               )}
               <div ref={bottom}></div>
            </div>
            <div className="chat-form">
               <textarea
                  className="input input-modals"
                  placeholder="Escribe una respuesta para el propietario"
                  maxLength={MAX_TEXT}
                  value={text}
                  onChange={(e) => setText(e.target.value)}></textarea>
            </div>
            {preview && (
               <div className="chat-preview">
                  <img src={preview} alt="Foto por enviar" title="Ver en grande" onClick={() => setZoom(preview)} />
                  <button type="button" className="chat-preview-remove" title="Quitar foto" onClick={removeImage}>
                     <i className="fas fa-times"></i>
                  </button>
               </div>
            )}
            <div className="white-space-8"></div>
            <div className="row align-center">
               <label className="btn btn-small btn-secondary color-white" style={{ cursor: 'pointer' }}>
                  <i className="fas fa-camera"></i>&nbsp; {image ? 'Cambiar foto' : 'Foto'}
                  <input ref={fileInput} type="file" accept="image/jpeg,image/png" hidden onChange={pickImage} />
               </label>
               &nbsp;&nbsp;
               <span className="chat-file">{`${text.length}/${MAX_TEXT}`}</span>
            </div>
            {/* Portal: el modal es arrastrable (transform) y un fixed adentro
                quedaría atrapado en él. */}
            {zoom &&
               createPortal(
               <div className="chat-zoom" onClick={() => setZoom('')}>
                  <img src={zoom} alt="Foto en grande" onClick={(e) => e.stopPropagation()} />
                  <button type="button" className="chat-zoom-close" title="Cerrar" onClick={() => setZoom('')}>
                     <i className="fas fa-times"></i>
                  </button>
               </div>,
                  document.body
               )}
         </div>
      </ModalContainer>
   );
};

export default RequestChat;
